import { useEffect } from 'react';

/**
 * Keep the screen awake while `active` is true (e.g. during the ~80s portrait
 * generation, so a phone doesn't sleep mid-render). Uses the Screen Wake Lock
 * API where available; silently no-ops otherwise. Re-acquires the lock when the
 * tab becomes visible again (the browser drops it when the page is hidden).
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return;

    let sentinel: { release: () => Promise<void> } | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const s = await nav.wakeLock!.request('screen');
        if (cancelled) {
          s.release().catch(() => {});
        } else {
          sentinel = s;
        }
      } catch {
        /* user denied / not allowed (e.g. low battery) — ignore */
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled && !sentinel) acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [active]);
}
